/**
 * Currency API service – Frankfurter (European Central Bank reference rates).
 * No API key, no tracking, no usage caps. Data from ECB.
 * @see https://www.frankfurter.app/docs/
 */

const FRANKFURTER_BASE = 'https://api.frankfurter.dev/v1'

export interface LatestRatesResponse {
  base: string
  date: string
  rates: Record<string, number>
}

export interface CurrenciesResponse {
  [code: string]: string
}

export interface ApiError {
  message: string
  status?: number
}

async function fetchApi<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) {
    throw { message: res.statusText || 'Request failed', status: res.status } as ApiError
  }
  return res.json() as Promise<T>
}

/**
 * Latest exchange rates. Base currency defaults to EUR.
 * Symbols limit returned currencies (e.g. USD,EUR,GBP).
 */
export async function getLatestRates(
  base = 'EUR',
  symbols?: string[]
): Promise<LatestRatesResponse> {
  const params = new URLSearchParams({ base })
  if (symbols?.length) params.set('symbols', symbols.join(','))
  return fetchApi<LatestRatesResponse>(`${FRANKFURTER_BASE}/latest?${params}`)
}

/**
 * Supported currency codes and full names. No tracking.
 */
export async function getCurrencies(): Promise<CurrenciesResponse> {
  return fetchApi<CurrenciesResponse>(`${FRANKFURTER_BASE}/currencies`)
}

/**
 * Convert amount from one currency to another using latest rate.
 * Rate is fetched from Frankfurter; conversion is done client-side.
 */
export async function convertCurrency(
  from: string,
  to: string,
  amount: number
): Promise<{ from: string; to: string; amount: number; result: number; rate: number; date: string }> {
  const data = await getLatestRates(from, [to])
  const rate = data.rates[to]
  if (rate == null) throw { message: `Rate for ${to} not found` } as ApiError
  return {
    from,
    to,
    amount,
    result: Math.round(amount * rate * 100) / 100,
    rate,
    date: data.date,
  }
}